<template>
  <box-default>
    <div class="col-xs-12">
      <div class="default-box pi-page">
        <h1 class="pi-page__title">Product Intelligence</h1>
        <pi-tabs current="features"/>
        <pi-filters v-model="filters" :fields="filterFields" @clear="clearFilters"/>

        <section class="pi-section" aria-labelledby="pi-features-table">
          <div class="pi-section__head">
            <h2 id="pi-features-table" class="pi-section__title">Adoção por funcionalidade</h2>
            <pi-state-legend/>
            <p class="pi-section__hint">{{ periodText }} · elegível = habilitado de empresa com a funcionalidade ativa e perfil aplicável</p>
            <pi-export-menu title="Adoção por funcionalidade" screen="Funcionalidades" :columns="columns" :rows="visibleRows" :envelope="request.envelope"
                            :filters="exportFilters" :period="exportPeriodValue" :disabled="!visibleRows.length || !!request.error"/>
          </div>

          <pi-data-state :loading="request.loading" :error="request.error" :envelope="request.envelope" :empty="isEmpty"
                         :show-footer="false" @retry="load(true)">
            <div class="pi-features__summary" role="group" aria-label="Filtrar por classificação">
              <span v-for="item in summary" :key="item.value" class="pi-features__chip-wrap">
                <button type="button" class="pi-features__chip"
                        :class="{ 'is-on': classification === item.value }" :title="item.description"
                        :aria-pressed="classification === item.value ? 'true' : 'false'" @click="toggleClassification(item.value)">
                  <span class="pi-badge" :class="'pi-badge--' + item.tone">{{ item.label }}</span>
                  <span class="pi-features__count">{{ item.count }}</span>
                </button>
                <pi-definition-tooltip :info-key="item.infoKey" :label="item.label"/>
              </span>
            </div>

            <div class="pi-toolbar">
              <div class="pi-toolbar__field">
                <span><label class="pi-toolbar__text" :for="ids.search">Buscar</label><pi-definition-tooltip class="pi-features__info" info-key="features.search" label="Buscar funcionalidade"/></span>
                <input :id="ids.search" v-model="search" type="search" class="form-control pi-input" maxlength="60"
                       placeholder="Nome, módulo ou número">
              </div>
              <filter-select label="Classificação" :value="classification" :options="classificationOptions" all-label="Todas"
                             @input="classification = $event">
                <pi-definition-tooltip slot="info" class="pi-features__info" info-key="features.classification" label="Classificação"/>
              </filter-select>
              <filter-select label="Estado" :value="stateFilter" :options="stateOptions" all-label="Todos"
                             @input="stateFilter = $event">
                <pi-definition-tooltip slot="info" class="pi-features__info" info-key="features.state" label="Estado"/>
              </filter-select>
              <a v-if="localFilterActive" href="#" class="pi-link pi-features__clear" @click.prevent="clearLocal">Limpar busca</a>
              <span class="pi-muted" role="status">{{ countText }}</span>
            </div>

            <paged-table v-if="visibleRows.length" :rows="visibleRows" storage-key="pi-features" :default-page-size="25"
                         v-slot="{ rows }">
              <thead>
              <tr>
                <template v-for="column in headerColumns">
                  <pi-sort-th v-if="column.sortable" :key="column.key" :column="column" :state="sort" @sort="sortBy"/>
                  <pi-th v-else :key="column.key" :column="column"/>
                </template>
              </tr>
              </thead>
              <tbody>
              <tr v-for="row in rows" :key="row.key" class="pi-row-link" @click="openRow(row, $event)">
                <td>
                  <router-link v-if="row.route" :to="detailRoute(row)" class="pi-link">{{ row.name }}</router-link>
                  <span v-else>{{ row.name }}</span>
                  <span class="pi-table__sub">{{ row.moduleText }}<template v-if="row.featureType !== null"> · nº {{ row.featureType }}</template></span>
                </td>
                <td class="is-num">
                  {{ count(row.eligibleCompanies) }}
                  <span v-if="row.configured" class="pi-table__sub" title="Empresas com a funcionalidade configurada hoje, pela foto diária">{{ row.configured.text }}</span>
                </td>
                <td class="is-num">
                  {{ count(row.adoptingCompanies) }}
                  <span class="pi-table__sub">{{ row.companyAdoption.text }} das elegíveis</span>
                </td>
                <td class="is-num">{{ count(row.eligibleUsers) }}</td>
                <td class="is-num">{{ count(row.adoptingUsers) }}</td>
                <td class="is-num"><pi-metric-cell :cell="row.adoption" :show-ratio="false"/></td>
                <td class="is-num">{{ row.adoptionDeltaText }}</td>
                <td class="is-num"><pi-metric-cell :cell="row.recurrence" :show-reason="false"/></td>
                <td class="is-num"><pi-metric-cell :cell="row.frequency" :show-reason="false"/></td>
                <td class="is-num"><pi-metric-cell :cell="row.intensity" :show-reason="false"/></td>
                <td class="is-num"><pi-metric-cell :cell="row.successRate" :show-ratio="false" :show-reason="!row.successRate.visible"/></td>
                <td>
                  <span class="pi-badge" :class="'pi-badge--' + row.classification.tone" :title="row.classification.description">
                    {{ row.classification.label }}
                  </span>
                </td>
                <td><pi-sparkline :points="row.trend" unit="pct" :label="'Adoção de ' + row.name" :width="96" :height="28"/></td>
                <td>
                  <span class="pi-badge" :class="'pi-badge--' + row.state.tone" :title="row.state.description">{{ row.state.label }}</span>
                  <span v-if="row.adoption.reason" class="pi-table__sub">{{ row.adoption.reason }}</span>
                </td>
              </tr>
              </tbody>
            </paged-table>
            <p v-else class="pi-muted">Nenhuma funcionalidade para a busca e os filtros escolhidos.</p>

            <p class="pi-muted pi-features__legend">
              Frequência = ações por adotante por semana. Intensidade = dias com uso no mês por adotante (mediana).
              Recorrência = adotantes com uso em 2 ou mais semanas do mês. Δ em pontos percentuais contra o período anterior.
              Customizações por cliente e flags técnicas ficam fora.
            </p>
          </pi-data-state>
        </section>

        <pi-page-footer :envelopes="[request.envelope]"/>
      </div>
    </div>
  </box-default>
</template>

<script>
import boxDefault from "../../components/layouts/boxDefault.vue";
import pagedTable from "../../components/default/pagedTable.vue";
import filterSelect from "../../components/default/filterSelect.vue";
import piTabs from "../../components/pi/piTabs.vue";
import piFilters from "../../components/pi/piFilters.vue";
import piDataState from "../../components/pi/piDataState.vue";
import piSparkline from "../../components/pi/piSparkline.vue";
import piMetricCell from "../../components/pi/piMetricCell.vue";
import piSortTh from "../../components/pi/piSortTh.vue";
import piTh from "../../components/pi/piTh.vue";
import piPageFooter from "../../components/pi/piPageFooter.vue";
import piDefinitionTooltip from "../../components/pi/piDefinitionTooltip.vue";
import piStateLegend from "../../components/pi/piStateLegend.vue";
import piExportMenu from "../../components/pi/piExportMenu.vue";
import { listFilterState } from "../../components/default/listFilterState";
import { piGet } from "../../components/pi/piApi.js";
import { screenColumns } from "../../components/pi/piColumns.js";
import { exportPeriod, exportProvide, filterLines } from "../../components/pi/piExport.js";
import { formatInteger, formatPeriod } from "../../components/pi/piFormat.js";
import { PI_FILTER_DEFAULTS, PI_FILTER_KEY, buildApiQuery, compareEnabled, nextSort, resolvePeriod, toQueryString } from "../../components/pi/piQuery.js";
import { blankRequest, listPayloadOf, listOf, payloadOf, runRequest, shouldLoad } from "../../components/pi/piRequests.js";
import { countText } from "../../components/pi/piTable.js";
import {
  CLASSIFICATIONS, DEFAULT_FEATURE_SORT, FEATURE_COLUMNS, NO_CLASSIFICATION, classificationInfoKey, classificationSummary, featureListLines,
  featureRows, stateFilterOptions, visibleFeatureRows,
} from "../../components/pi/piFeaturesModel.js";
import { PI_FEATURES } from "../../../../urlConfig";

const FILTER_FIELDS = ["period", "compare", "module", "roleClass", "sizeBand", "segment"];

export default {
  name: "pi-features",
  components: {
    boxDefault, pagedTable, filterSelect, piTabs, piFilters, piDataState, piSparkline, piMetricCell, piSortTh, piTh, piPageFooter, piDefinitionTooltip,
    piStateLegend, piExportMenu,
  },
  mixins: [listFilterState(PI_FILTER_KEY, PI_FILTER_DEFAULTS)],
  provide: exportProvide,
  data() {
    return {
      ids: { search: "pi-features-search" },
      filterFields: FILTER_FIELDS,
      request: blankRequest(),
      search: "",
      classification: "",
      stateFilter: "",
      sort: Object.assign({}, DEFAULT_FEATURE_SORT),
      columns: FEATURE_COLUMNS,
      headerColumns: screenColumns(FEATURE_COLUMNS),
      classificationOptions: CLASSIFICATIONS.concat([NO_CLASSIFICATION]).map((item) => ({ value: item.value, label: item.label })),
    };
  },
  computed: {
    compare() {
      return compareEnabled(this.filters);
    },
    periodText() {
      const period = resolvePeriod(this.filters, new Date());
      return formatPeriod(period.from, period.to);
    },
    params() {
      return buildApiQuery(this.filters, { fields: FILTER_FIELDS });
    },
    apiKey() {
      return toQueryString(this.params);
    },
    sourceRows() {
      const data = payloadOf(this.request.envelope);
      const rows = Array.isArray(data.rows) ? data.rows : listPayloadOf(this.request.envelope);
      return listOf(rows);
    },
    allRows() {
      return featureRows(this.sourceRows, this.compare);
    },
    visibleRows() {
      return visibleFeatureRows(this.allRows, { search: this.search, classification: this.classification, state: this.stateFilter }, this.sort);
    },
    summary() {
      return classificationSummary(this.allRows).map((item) => Object.assign({}, item, { infoKey: classificationInfoKey(item.value) }));
    },
    exportFilters() {
      return filterLines({
        filters: this.filters,
        fields: FILTER_FIELDS,
        extra: featureListLines({ search: this.search, classification: this.classification, state: this.stateFilter }, this.sort),
      });
    },
    exportPeriodValue() {
      return exportPeriod(this.filters);
    },
    stateOptions() {
      return stateFilterOptions(this.allRows);
    },
    localFilterActive() {
      return !!(this.search || this.classification || this.stateFilter);
    },
    countText() {
      const total = this.allRows.length;
      const shown = this.visibleRows.length;
      return shown === total
        ? formatInteger(total) + (total === 1 ? " funcionalidade" : " funcionalidades")
        : formatInteger(shown) + " de " + formatInteger(total) + " funcionalidades";
    },
    isEmpty() {
      return this.request.loaded && !this.allRows.length;
    },
  },
  watch: {
    apiKey() {
      this.load(false);
    },
  },
  created() {
    this.load(false);
  },
  methods: {
    load(force) {
      const key = this.apiKey;
      if (!shouldLoad(this.request, key, force)) return;
      runRequest(this.request, () => piGet(PI_FEATURES, this.params), "carregar as funcionalidades", key);
    },
    count(value) {
      return countText(value);
    },
    sortBy(column) {
      this.sort = nextSort(this.sort, column.key, column.startDescending);
    },
    toggleClassification(value) {
      this.classification = this.classification === value ? "" : value;
    },
    clearLocal() {
      this.search = "";
      this.classification = "";
      this.stateFilter = "";
    },
    detailRoute(row) {
      return { path: row.route, query: this.$route.query };
    },
    openRow(row, event) {
      if (!row.route) return;
      const target = event && event.target;
      if (target && typeof target.closest === "function" && target.closest("a, button, input, select")) return;
      this.$router.push(this.detailRoute(row)).catch(() => {});
    },
  },
};
</script>

<style src="../../components/pi/pi.css"></style>

<style scoped>
.pi-features__summary {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}

.pi-features__chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 32px;
  padding: 0 10px;
  border: 1px solid #d9dde3;
  border-radius: 16px;
  background: #ffffff;
  font-size: 12px;
  color: #3a424e;
  cursor: pointer;
}

.pi-features__chip.is-on {
  border-color: #1b59f8;
  box-shadow: 0 0 0 1px #1b59f8;
}

.pi-features__count {
  font-weight: 700;
}

.pi-features__chip-wrap {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.pi-features__info {
  margin-left: 4px;
}

.pi-features__clear {
  padding-bottom: 8px;
  font-size: 12px;
}

.pi-features__legend {
  margin-top: 10px;
}
</style>



// WEBPACK FOOTER //
// src/vue/pages/restricted/productIntelligence/piFeatures.vue